import React, { useEffect, useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import { useOrdersStore } from "../../stores/orders/orders-store";
import {
  initializeOrderConsole,
  displayOrders,
  changeView,
  merchantLogout
} from "../../stores/orders/orders-actions";
import OrderStatusSection from "./OrderStatusSection";
import ViewFilterModal from "./ViewFilterModal";
import ErrorModal from "./ErrorModal";
import FilterListIcon from "@mui/icons-material/FilterList";

const OrderConsoleContainer = styled("div")({
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  background: "#F2F2F0",
  fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif'
});

const Header = styled("div")({
  background: "#223354",
  width: "100%",
  height: "60px",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: "0 20px",
  borderBottom: "1px solid #000"
});

const OrdersContainer = styled("div")({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden"
});

const ViewFilterButton = styled("button")({
  background: "transparent",
  border: "none",
  color: "#fff",
  cursor: "pointer",
  padding: "12px 16px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontSize: "16px",
  borderRadius: "8px",
  "&:hover": {
    opacity: 0.9,
    backgroundColor: "rgba(255,255,255,0.1)"
  },
  transition: "all 0.2s ease"
});

const OrderConsole: React.FC = () => {
  const {
    orderStatusSettings,
    visibleViews,
    activeView,
    isErrorModalOpen,
    errorMessage,
    businessName,
    isLoading,
    businessId,
    hasInteracted
  } = useOrdersStore();
  console.log("🚀 ~ OrderConsole ~ hasInteracted:", hasInteracted);

  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  useEffect(() => {
    // Only initialize if businessId is present in the store and not empty
    if (businessId && businessId.trim() !== "") {
      console.log("Initializing order console with businessId:", businessId);
      initializeOrderConsole();
    } else {
      console.log("No valid businessId found, skipping initialization");
    }

    const intervalId = setInterval(() => {
      if (businessId && businessId.trim() !== "") {
        displayOrders();
      }
    }, 20000); // 20 seconds as in original
    refreshIntervalRef.current = intervalId;
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [businessId]);

  const handleViewChange = (viewType: string) => {
    changeView(viewType);
  };

  const handleViewFilterToggle = () => {
    setIsViewModalOpen(!isViewModalOpen);
  };

  if (!businessId && !isLoading) {
    return (
      <OrderConsoleContainer>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            color: "#c00",
            fontWeight: "bold"
          }}
        >
          Error: Business ID could not be found. Please log in again.
        </div>
      </OrderConsoleContainer>
    );
  }

  if (isLoading) {
    return (
      <OrderConsoleContainer>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%"
          }}
        >
          Loading Order Console...
        </div>
      </OrderConsoleContainer>
    );
  }

  return (
    <OrderConsoleContainer>
      <Header id="header-container">
        <ViewFilterButton onClick={handleViewFilterToggle}>
          <FilterListIcon />
          View Orders
        </ViewFilterButton>
      </Header>

      {/* Audio interaction notification */}
      {!hasInteracted && (
        <div
          style={{
            background: "#FF9800",
            color: "white",
            padding: "8px 16px",
            textAlign: "center",
            fontSize: "14px",
            fontWeight: "bold"
          }}
        >
          🔊 Click anywhere to enable sound notifications for new orders
        </div>
      )}

      <OrdersContainer>
        {orderStatusSettings.map((setting: any) => {
          const statusId = `${setting.SettingId}_${setting.SettingName.split(
            " "
          ).join("")}`;
          const isVisible =
            visibleViews.includes(statusId) || activeView === "All";

          return (
            <OrderStatusSection
              key={statusId}
              statusId={statusId}
              statusName={setting.SettingName}
              isVisible={isVisible}
            />
          );
        })}
      </OrdersContainer>

      <ViewFilterModal
        orderStatusSettings={orderStatusSettings}
        activeView={activeView}
        onViewChange={handleViewChange}
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
      />

      <ErrorModal isOpen={isErrorModalOpen} message={errorMessage} />
    </OrderConsoleContainer>
  );
};

export default OrderConsole;
